// Getting Started progress: what a dealer has done, and what it adds up to for each step.
//
// Strapi says what the steps and their tasks are; `data.about.onboarding` on the user record says which tasks this
// dealer has done ({ started: { [stepKey]: iso }, tasks: { [taskKey]: { completed_at, source } }, completed: { [stepKey]: { completed_at, source } },
// first_sale_at, backfilled_at }); a step's status is worked out here from its tasks, or from the mark the dealer made
// by hand (`completed`), and never stored, so it can't disagree with what it is made of.
import {
  ONBOARDING_MARKETPLACES,
  ONBOARDING_STEP_STATUS,
  ONBOARDING_TASK,
  onboardingConnectTask,
  onboardingSettingsTask,
} from '@/constants'

export const onboardingProgressOf = (user) => {
  const progress = user?.data?.about?.onboarding

  return { ...progress, started: { ...progress?.started }, tasks: { ...progress?.tasks }, completed: { ...progress?.completed } }
}

// The legacy Home's capture-actions, which the backend records on the user. They are how a dealer who did these
// things before progress was tracked still gets credit for them.
const TASKS_BY_CAPTURED_ACTION = {
  'created-batch': ONBOARDING_TASK.BATCH_CREATED,
  'prepared-export': ONBOARDING_TASK.CARDS_REVIEWED,
  'created-export': ONBOARDING_TASK.CARD_LISTED,
}

// A marketplace import that has finished with something in it.
const hasFinishedImport = (importProgress) => {
  const imports = typeof importProgress === 'string' ? JSON.parse(importProgress || '{}') : importProgress

  return Object.values(imports ?? {}).some((entry) =>
    entry && !entry.noItems && (entry.active === false || entry.progress === 100) && Number(entry.totalItems ?? 1) > 0)
}

// The tasks the user record already proves done: connected marketplaces, the captured actions, a finished import.
// Read on every load, so a task that finished while no tab was open (an import, an OAuth return) is still ticked.
export const onboardingTasksFromRecord = (user, connectedMarketplaces = []) => {
  const tasks = new Set()

  connectedMarketplaces
    .filter((marketplace) => ONBOARDING_MARKETPLACES.includes(marketplace))
    .forEach((marketplace) => tasks.add(onboardingConnectTask(marketplace)))

  for (const action of user?.actions_completed ?? []) {
    if (TASKS_BY_CAPTURED_ACTION[action]) tasks.add(TASKS_BY_CAPTURED_ACTION[action])
  }

  try {
    if (hasFinishedImport(user?.import_progress)) tasks.add(ONBOARDING_TASK.IMPORT_COMPLETED)
  } catch {
    // A malformed import_progress proves nothing either way.
  }

  return [...tasks]
}

// A step's tasks, each marked done or not, and the step's status. Every required task must be done; of the rest,
// `completeWhen: 'any'` needs one and `'all'` (the default) needs every one. A step with no tasks is never complete
// on its own. The dealer can also mark a whole step complete by hand; its tasks still say what really happened.
export const onboardingStepProgress = (step, progress) => {
  const tasks = (step.tasks ?? []).map((task) => ({ ...task, completed: !!progress.tasks[task.key] }))
  const required = tasks.filter((task) => task.required)
  const optional = tasks.filter((task) => !task.required)
  const marked = !!progress.completed[step.key]

  const optionalDone = step.completeWhen === 'any'
    ? !optional.length || optional.some((task) => task.completed)
    : optional.every((task) => task.completed)

  const complete = marked || (tasks.length > 0 && required.every((task) => task.completed) && optionalDone)
  const started = !!progress.started[step.key] || tasks.some((task) => task.completed)

  const status = complete
    ? ONBOARDING_STEP_STATUS.COMPLETE
    : started ? ONBOARDING_STEP_STATUS.IN_PROGRESS : ONBOARDING_STEP_STATUS.NOT_STARTED

  return { tasks, status }
}

// The Getting Started tasks a card update completes: setting a card to Ready is "Mark a card ready to list".
// `ready_to_list` is the backend value behind CARD_STATUS_META's "Ready".
export const onboardingTasksForCardUpdate = (body) =>
  (body?.status === 'ready_to_list' ? [ONBOARDING_TASK.CARDS_REVIEWED] : [])

// Which marketplace a saved marketplace template belongs to, by the ConnectionStore list it is kept in.
const MARKETPLACE_BY_TEMPLATE_LIST = {
  listingTemplates: 'ebay',
  marketplaceListingTemplates: 'ebay',
  whatnotListingTemplates: 'whatnot',
  collxListingTemplates: 'collx',
  shopifyListingTemplates: 'shopify',
}

// Saving a marketplace template is that marketplace's "Set up … settings".
export const onboardingTasksForTemplateList = (templateList) => {
  const marketplace = MARKETPLACE_BY_TEMPLATE_LIST[templateList]

  return marketplace ? [onboardingSettingsTask(marketplace)] : []
}

// Whether a dealer has finished a step: its tasks say so, or the dev toggle pretends it (`completedSteps`).
export const isOnboardingStepComplete = (step, progress, completedSteps = []) =>
  completedSteps.includes(step.key) || onboardingStepProgress(step, progress).status === ONBOARDING_STEP_STATUS.COMPLETE
